import React, { useState, useMemo } from 'react'
import { Box, Card, Flex, Grid, Text } from '@chakra-ui/react'
import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import MasterTable, { Column, RowData } from './base/MasterTable'
import SearchInput from './base/SearchInput'
import FilterButton from './base/FilterButton'
import Pagination from './base/Pagination'
import LoadingTable from './base/LoadingTable'
import formatDate from '../../helpers/formatDate'
import AvatarCell from './base/AvatarCell'
import { fetchProfileChanges } from '../../api/profileChanges'
import { fetchEmployeeList } from '../../api/workforce/workforce'
import { useTranslation } from 'react-i18next'

interface MutationUser {
  first_name?: string
  last_name?: string
  work_email?: string
  id?: string | number
  profile_picture?: string
}

function getUserDisplay(user: MutationUser | string | null | undefined, naLabel = '') {
  if (!user) return { name: naLabel, avatar: undefined }
  if (typeof user === 'string') return { name: user, avatar: undefined }
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.work_email || String(user.id || '')
  const avatar = user.profile_picture
  return { name, avatar }
}

function formatJsonValue(val: unknown): string {
  try {
    if (typeof val === 'string') return val
    return JSON.stringify(val) ?? String(val)
  } catch {
    return String(val)
  }
}

// For specific fields like Job Profile, display the label when value is an object
function formatValueForField(t: (key: string, options?: Record<string, unknown>) => string, fieldName: string | undefined, val: unknown): string {
  try {
    if (fieldName === 'Current Job Profile' && val && typeof val === 'object') {
      const label = (val as Record<string, unknown>).label
      if (typeof label === 'string') return label
    }
    if (fieldName === 'FTE (%)') return `${val}%`
    if (fieldName === 'Contract Type' && val) {
      if (typeof val === 'string' && val === 'FT') return t('workforce.mutations.contractTypes.fullTime')
      else if (typeof val === 'string' && val === 'PT') return t('workforce.mutations.contractTypes.partTime')
      else if (typeof val === 'string' && val === 'CT') return t('workforce.mutations.contractTypes.contract')
      else if (typeof val === 'string' && val === 'IN') return t('workforce.mutations.contractTypes.internship')
      else if (typeof val === 'string' && val === 'FL') return t('workforce.mutations.contractTypes.freelance')
    }
  } catch { }
  return formatJsonValue(val)
}

interface MutationsTableProps {
  employeeId?: string
}

const MutationsTable: React.FC<MutationsTableProps> = ({ employeeId }) => {

  const { t } = useTranslation()

  const [page, setPage] = useState(1)
  const itemsPerPage = 15
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState<{ [key: string]: string | string[] }>({})

  const updateFilter = (key: string, values: string[]) => {
    setFilters(prev => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      if (employeeId) next.employee = [employeeId]
      return next
    })
    setPage(1)
  }

  // If rendering inside a user's profile, lock the employee filter to that user
  React.useEffect(() => {
    if (employeeId) {
      setPage(1)
      setFilters((prev) => ({ ...prev, employee: [employeeId] }))
    }
  }, [employeeId])

  // --- Filter: Employee (User) - infinite scroll, only when no employeeId ---
  const [employeeFilterSearch, setEmployeeFilterSearch] = useState('')
  const queryClient = useQueryClient()
  const employeeFilterQuery = useInfiniteQuery({
    queryKey: ['filter-employees', 10, employeeFilterSearch],
    queryFn: ({ pageParam }) =>
      fetchEmployeeList({
        search: employeeFilterSearch, page: pageParam, pageSize: 10, filters: {}, minimal: true,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.has_next ? allPages.length + 1 : undefined,
    enabled: !employeeId,
    staleTime: 30000,
  })
  const employeeFilterList = useMemo(
    () =>
      (employeeFilterQuery.data?.pages ?? []).flatMap((p: { data?: { id: string; first_name: string; last_name: string }[] }) =>
        Array.isArray(p?.data)
          ? p.data.map((e) => ({ id: e.id, name: `${e.first_name} ${e.last_name}` }))
          : [],
      ),
    [employeeFilterQuery.data?.pages],
  )

  // --- Filter: Changed By - infinite scroll ---
  const [changedByFilterSearch, setChangedByFilterSearch] = useState('')
  const changedByFilterQuery = useInfiniteQuery({
    queryKey: ['filter-changed-by', 10, changedByFilterSearch],
    queryFn: ({ pageParam }) =>
      fetchEmployeeList({ search: changedByFilterSearch, page: pageParam, pageSize: 10, filters: {}, minimal: true }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.has_next ? allPages.length + 1 : undefined,
  })
  const changedByFilterList = useMemo(
    () =>
      (changedByFilterQuery.data?.pages ?? []).flatMap((p: { data?: { id: string; first_name: string; last_name: string }[] }) =>
        Array.isArray(p?.data)
          ? p.data.map((e) => ({ id: e.id, name: `${e.first_name} ${e.last_name}` }))
          : [],
      ),
    [changedByFilterQuery.data?.pages],
  )

  const mutations = useQuery({
    queryKey: ['profileChanges', page, itemsPerPage, filters, searchTerm],
    queryFn: async () => {
      const apiFilters: { [key: string]: string | string[] } = {}
      if (filters.employee) apiFilters.employee = filters.employee
      if (filters.changed_by) apiFilters.changed_by = filters.changed_by
      // Treat search term as field_name filter for convenience
      if (searchTerm) apiFilters.field_name = searchTerm
      if (filters.field_name && !searchTerm) {
        const v = filters.field_name
        apiFilters.field_name = v
      }
      if (filters.date_from) apiFilters.date_from = Array.isArray(filters.date_from) ? filters.date_from[0] : filters.date_from
      if (filters.date_to) apiFilters.date_to = Array.isArray(filters.date_to) ? filters.date_to[0] : filters.date_to
      return fetchProfileChanges({ page, pageSize: itemsPerPage, filters: apiFilters })
    },
    staleTime: 30000,
  })

  // Prefetch next page of mutations for snappier pagination
  React.useEffect(() => {
    if (!mutations?.data) return
    const maxPage = mutations.data.max_page || 0
    const nextPage = page + 1
    if (nextPage <= maxPage) {
      const nextKey = ['profileChanges', nextPage, itemsPerPage, filters, searchTerm]
      try {
        queryClient.prefetchQuery({
          queryKey: nextKey,
          queryFn: () => {
            const apiFilters: { [key: string]: string | string[] } = {}
            if (filters.employee) apiFilters.employee = filters.employee
            if (filters.changed_by) apiFilters.changed_by = filters.changed_by
            if (searchTerm) apiFilters.field_name = searchTerm
            if (filters.field_name && !searchTerm) apiFilters.field_name = filters.field_name
            if (filters.date_from) apiFilters.date_from = Array.isArray(filters.date_from) ? filters.date_from[0] : filters.date_from
            if (filters.date_to) apiFilters.date_to = Array.isArray(filters.date_to) ? filters.date_to[0] : filters.date_to
            return fetchProfileChanges({ page: nextPage, pageSize: itemsPerPage, filters: apiFilters })
          },
          staleTime: 30000,
        })
      } catch (err) {
        console.warn('Mutations prefetch failed', err)
      }
    }
  }, [mutations?.data, page, searchTerm, filters, queryClient])

  // Field Name options as requested
  const fieldNameOptions = useMemo(() => [
    { id: 'Current Job Profile', name: t('workforce.mutations.fields.currentJobProfile') },
    { id: 'Contract Type', name: t('workforce.mutations.fields.contractType') },
    { id: 'Contract Duration', name: t('workforce.mutations.fields.contractDuration') },
    { id: 'Work Hours per Week', name: t('workforce.mutations.fields.workHoursPerWeek') },
    { id: 'Work Hours in Office', name: t('workforce.mutations.fields.workHoursInOffice') },
    { id: 'Work Hours from Home', name: t('workforce.mutations.fields.workHoursFromHome') },
    { id: 'FTE (%)', name: t('workforce.mutations.fields.ftePercent') },
    { id: 'Salary', name: t('workforce.mutations.fields.salary') },
    { id: 'Benefits (all)', name: t('workforce.mutations.fields.benefitsAll') },
  ], [t])

  const columns: Column[] = [
    {
      label: t('common.fields.user'),
      accessor: 'employee',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => {
        const { name, avatar } = getUserDisplay(row.employee, t('common.labels.na'))
        return (
          <AvatarCell image={avatar} name={name} />
        )
      },
    },
    {
      label: t('workforce.mutations.fieldName'),
      accessor: 'field_name',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => <Text textStyle="l-xs" isTruncated>{row.field_name || ''}</Text>,
    },
    {
      label: t('workforce.mutations.mutationDate'),
      accessor: 'created_at',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs">{row.created_at ? formatDate(row.created_at) : ''}</Text>
      ),
    },
    {
      label: t('workforce.mutations.previousValue'),
      accessor: 'previous_value',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => {
        const prev = row.previous_value
        if (prev === null || prev === undefined) {
          return <Text textStyle="l-xs">{t('common.labels.na')}</Text>
        }
        const txt = formatValueForField(t, row.field_name, prev)
        return <Text textStyle="l-xs" isTruncated title={txt} maxW="360px">{txt}</Text>
      },
    },
    {
      label: t('workforce.mutations.newValue'),
      accessor: 'new_value',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => {
        const txt = formatValueForField(t, row.field_name, row.new_value)
        return (
          <Text textStyle="l-xs" isTruncated title={txt} maxW="360px">{txt}</Text>
        )
      },
    },
    {
      label: t('workforce.mutations.changedBy'),
      accessor: 'changed_by',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => {
        const { name, avatar } = getUserDisplay(row.changed_by)
        return (
          <AvatarCell image={avatar} name={name} />
        )
      },
    },
  ]

  const tableData = mutations?.data?.data || []

  return (
    <Card width="100%" p="0">
      <Grid>
        <Box>
          <Flex justifyContent="space-between" alignItems="center" height="64px" flexWrap="wrap" gap={4} px="20px">
            <SearchInput onSearch={(term) => { setSearchTerm(term); setPage(1) }} />
            <Flex gap="8px">
              {!employeeId && (
                <FilterButton
                  label={t('common.fields.user')}
                  options={employeeFilterList}
                  onApply={(selected) => updateFilter('employee', selected)}
                  search={employeeFilterSearch}
                  setSearch={setEmployeeFilterSearch}
                  onLoadMore={() => employeeFilterQuery.fetchNextPage()}
                  hasMore={!!employeeFilterQuery.hasNextPage}
                  isLoadingMore={employeeFilterQuery.isFetchingNextPage}
                  isLoading={employeeFilterQuery.isFetching && !employeeFilterQuery.isFetchingNextPage}
                />
              )}
              <FilterButton
                label={t('workforce.mutations.changedBy')}
                options={changedByFilterList}
                onApply={(selected) => updateFilter('changed_by', selected)}
                search={changedByFilterSearch}
                setSearch={setChangedByFilterSearch}
                onLoadMore={() => changedByFilterQuery.fetchNextPage()}
                hasMore={!!changedByFilterQuery.hasNextPage}
                isLoadingMore={changedByFilterQuery.isFetchingNextPage}
                isLoading={changedByFilterQuery.isFetching && !changedByFilterQuery.isFetchingNextPage}
              />
              <FilterButton
                label={t('workforce.mutations.fieldName')}
                options={fieldNameOptions}
                onApply={(selected) => updateFilter('field_name', selected)}
              />
            </Flex>
          </Flex>
          {mutations.isLoading ? (
            <LoadingTable columns={columns} />
          ) : (
            <Box minHeight="58vh">
              <MasterTable
                columns={columns}
                data={tableData}
                onRowClick={() => { }}
                onOpenNewTab={() => { }}
                selectedRows={[]}
                onSelectAll={() => { }}
                onSelectRow={() => { }}
                allSelected={false}
                isIndeterminate={false}
                tableType={'people'}
              />
            </Box>
          )}
          <Pagination
            currentPage={page}
            itemsPerPage={itemsPerPage}
            maxPage={mutations?.data?.max_page || 0}
            totalItems={mutations?.data?.count || 0}
            handlePreviousPage={() => setPage(page - 1)}
            handleNextPage={() => setPage(page + 1)}
            handlePageClick={(p: number) => setPage(p)}
          />
        </Box>
      </Grid>
    </Card>
  )
}

export default MutationsTable
